/* ============================================================================
 * Parade Panic — Music Playlist Widget  (self-contained, brand-new)
 *
 * ON WEB: plays the built-in MP3s through the Web Audio API (fetch ->
 * decodeAudioData -> AudioBufferSourceNode). This path is used because the plain
 * HTMLAudioElement stalls on these particular MP3s in some browsers, whereas
 * decodeAudioData handles them reliably.
 *
 * ON iOS NATIVE: Web Audio's destination is silent inside Capacitor's WKWebView
 * (verified on device), so playback goes through @capgo/native-audio instead.
 * Tracks are preloaded on demand and the previous one unloaded — 50+ tracks at
 * ~1.7MB each makes preloading the whole playlist wasteful. An interim fix used
 * HTMLAudioElements here, which worked but kept several native media pipelines
 * alive and measurably cost framerate/battery; the plugin avoids that entirely.
 *
 * Fully independent of the rest of the game's audio: its own AudioContext, its
 * own gain, its own UI. Sound effects are unaffected. Nothing here autoplays —
 * audio only ever starts from a button/track click (a user gesture), which is
 * exactly what browser autoplay policies require.
 * ========================================================================== */
(function () {
  'use strict';

  const TRACKS = [
    { file: 'music/parade-panic.mp3',    title: 'Parade Panic (The OG March)', flavor: 'By The Float Inflators • Album: "Let\'s Get Pumped!" (Retro brass synth march)', cover: { bg: 'linear-gradient(135deg, #f53b57, #ff5e57)', icon: '🎈', shadow: 'rgba(245,59,87,0.4)' } },
    { file: 'music/Parade Panic.mp3',    title: 'Downtown VIP Strut',            flavor: 'By Sir Squeaks-a-Lot & The Balloon Guard • Album: "Grand Marshal\'s Velvet Rope"', cover: { bg: 'linear-gradient(135deg, #3c40c6, #5758bb)', icon: '👑', shadow: 'rgba(60,64,198,0.4)' } },
    { file: 'music/Parade Panic-2.mp3',  title: 'Hyper-Inflated Heliums',               flavor: 'By The High-Yield Horns • Album: "Interest Rate Parade" (Fast synth march)', cover: { bg: 'linear-gradient(135deg, #05c46b, #4bcffa)', icon: '💰', shadow: 'rgba(5,196,107,0.4)' } },
    { file: 'music/Parade Panic-3.mp3',  title: 'Tuba Lips & Soaring Hopes',                flavor: 'By The Spit-Valve Symphonette • Album: "Embossed Brass & Epaulets"', cover: { bg: 'linear-gradient(135deg, #ff3f34, #ef5777)', icon: '🎺', shadow: 'rgba(255,63,52,0.4)' } },
    { file: 'music/Parade Panic-4.mp3',  title: 'High-Pitch Hustle',              flavor: 'By The Chipmunk Woodwinds • Album: "My Kingdom For A Clarinut"', cover: { bg: 'linear-gradient(135deg, #00d2d3, #54a0ff)', icon: '🌀', shadow: 'rgba(0,210,211,0.4)' } },
    { file: 'music/Parade Panic-5.mp3',  title: 'Clown Shoes & Overdrafts',            flavor: 'By The Unicycle Brass Syndicate • Album: "Seltzer in Your Trombone"', cover: { bg: 'linear-gradient(135deg, #ffd32a, #ff9f43)', icon: '🤡', shadow: 'rgba(255,211,42,0.4)' } },
    { file: 'music/Parade Panic-6.mp3',  title: 'Lightning-Fast Left Foot',               flavor: 'By General Tempo\'s Electric Brigade • Album: "180 Beats Per Minute Or Bust"', cover: { bg: 'linear-gradient(135deg, #ffc048, #ff5e57)', icon: '⚡', shadow: 'rgba(255,192,72,0.4)' } },
    { file: 'music/Parade Panic-7.mp3',  title: 'Kernel\'s Command',                flavor: 'By The Popcorn Saxes • Album: "Butter on the Eb Key" (Bubbly pop beats)', cover: { bg: 'linear-gradient(135deg, #ffdd59, #ffd2af)', icon: '🍿', shadow: 'rgba(255,221,89,0.4)' } },
    { file: 'music/Parade Panic-8.mp3',  title: 'Tilt-A-Whirl Trombones',         flavor: 'By The Carny Brass Section • Album: "Vertigo & Funnel Cakes"', cover: { bg: 'linear-gradient(135deg, #a55eea, #fd79a8)', icon: '🎡', shadow: 'rgba(165,94,234,0.4)' } },
    { file: 'music/Parade Panic-9.mp3',  title: 'The Pride of the Parking Lot',                 flavor: 'By The Half-Step Marchers • Album: "We Practiced This For Months!"', cover: { bg: 'linear-gradient(135deg, #ff9f43, #ee5253)', icon: '🥁', shadow: 'rgba(255,159,67,0.4)' } },
    { file: 'music/Parade Panic-10.mp3', title: 'Midnight Drum-Major',                flavor: 'By The Glow-in-the-Dark Piccolos • Album: "LED Plumes & Battery Packs"', cover: { bg: 'linear-gradient(135deg, #2c3a47, #130cb7)', icon: '😎', shadow: 'rgba(19,12,183,0.4)' } },
    { file: 'music/Baton Rouge Static.mp3',        title: 'Zagreb Short-Circuit',             flavor: 'By The High-Voltage Second-Liners • Album: "Lightning Bolt Baton" (~185bpm)', cover: { bg: 'linear-gradient(135deg, #1dd1a1, #10ac84)', icon: '🔌', shadow: 'rgba(29,209,161,0.4)' } },
    { file: 'music/Bayou Bitstream.mp3',           title: 'Sub-Woofer Sousaphones',   flavor: 'By The Muddy Marsh Brass Band • Album: "Deep End of the Swamp" (Heavy bass)', cover: { bg: 'linear-gradient(135deg, #2f3640, #0a0c16)', icon: '💀', shadow: 'rgba(10,12,22,0.6)' } },
    { file: 'music/Bayou Bitstream-2.mp3',         title: 'Air-Cushioned Float Glide',           flavor: 'By The Fiberglass Chassis Orchestra • Album: "Gliding Past Stg. 4"', cover: { bg: 'linear-gradient(135deg, #e84118, #f5f6fa)', icon: '🏎️', shadow: 'rgba(232,65,24,0.4)' } },
    { file: 'music/Brass in the Balkan.mp3',       title: 'Pocket-Trumpet Pandemonium',           flavor: 'By The Clattering Clefs • Album: "Balkanized Boot-Stompers" (Short snap)', cover: { bg: 'linear-gradient(135deg, #485460, #1e272e)', icon: '🫰', shadow: 'rgba(30,39,46,0.4)' } },
    { file: 'music/Brass in the Balkan-2.mp3',     title: 'The Runaway Spit-Valves',        flavor: 'By The Galloping Gombas • Album: "Out of Breath, Out of Time" (Brass gallop)', cover: { bg: 'linear-gradient(135deg, #ff3838, #ffb8b8)', icon: '🏃', shadow: 'rgba(255,56,56,0.4)' } },
    { file: 'music/Glory in the Brass.mp3',        title: 'Pomp, Circumstance & Poor Posture',    flavor: 'By The Chest-Out Woodwinds • Album: "Golden Plumes & Shiny Trombones"', cover: { bg: 'linear-gradient(135deg, #eccc68, #ff7f50)', icon: '🦅', shadow: 'rgba(236,204,104,0.4)' } },
    { file: 'music/Glory in the Brass-2.mp3',      title: 'March of the Over-Decorated Marshals',     flavor: 'By The Royal Tuba Sovereigns • Album: "Heavier is the Crown (And the Sousaphone)"', cover: { bg: 'linear-gradient(135deg, #70a1ff, #5352ed)', icon: '👑', shadow: 'rgba(112,161,255,0.4)' } },
    { file: 'music/March of the Golden Eagles.mp3', title: 'March of the Flightless Eagles',     flavor: 'By The Mascot Uniform Brass • Album: "Locked in the Eagle Suit at 95°F"', cover: { bg: 'linear-gradient(135deg, #ff7f50, #ff6b81)', icon: '🪽', shadow: 'rgba(255,127,80,0.4)' } },
    { file: 'music/The Golden Horizon.mp3',        title: 'The Grand Parade Route',       flavor: 'By The Confetti Sweepers • Album: "Behind the Last Float" (Soaring glide)', cover: { bg: 'linear-gradient(135deg, #1e90ff, #3742fa)', icon: '🏙️', shadow: 'rgba(30,144,255,0.4)' } },
    { file: 'music/The Golden Horizon-2.mp3',      title: 'Sunburnt Drum Majors',    flavor: 'By The SPF-100 Brass • Album: "Sweating in Polyester Uniforms"', cover: { bg: 'linear-gradient(135deg, #ffa502, #ff7f50)', icon: '☀️', shadow: 'rgba(255,165,0,0.4)' } },
    { file: 'music/Brass Marching Chaos.mp3',      title: 'The Stampeding Spit-Valves',     flavor: 'By The Out-of-Tune Octave • Album: "Brass-Knuckle Marching Chaos" (~90bpm)', cover: { bg: 'linear-gradient(135deg, #cd6133, #57606f)', icon: '🐂', shadow: 'rgba(205,97,51,0.4)' } },
    { file: 'music/Brass Marching Chaos-2.mp3',    title: 'Trombone Slide Collision',         flavor: 'By The Frantic Fifth-Interval • Album: "Watch Your Teeth!" (~145bpm)', cover: { bg: 'linear-gradient(135deg, #cf6a87, #e15f41)', icon: '🔥', shadow: 'rgba(225,95,65,0.4)' } },
    { file: 'music/Brass Marching Chaos-3.mp3',    title: 'Dented Cymbals & Broken Sticks',    flavor: 'By The Clatter Cavalcade Percussion • Album: "Dropped the Baton Again"', cover: { bg: 'linear-gradient(135deg, #22a6b3, #30336b)', icon: '⚔️', shadow: 'rgba(34,166,179,0.4)' } },
    { file: 'music/Brass and Bone.mp3',            title: 'Skeletal Second Line',        flavor: 'By The Calcium Cadence • Album: "Bony Ribs & Slippery Slides" (Loud brass)', cover: { bg: 'linear-gradient(135deg, #718093, #2f3640)', icon: '☠️', shadow: 'rgba(113,128,147,0.4)' } },
    { file: 'music/Brass and Bone-2.mp3',          title: 'The Funny-Bone Fanfare',         flavor: 'By The Bone-Dry Brass • Album: "Tibia or Not Tibia" (Marrow march)', cover: { bg: 'linear-gradient(135deg, #ff4757, #ff6b81)', icon: '🦴', shadow: 'rgba(255,71,87,0.4)' } },
    { file: 'music/Brass March of the Djent.mp3',  title: 'Heavy Metal Marching Plume',          flavor: 'By The 8-String Sousaphones • Album: "Djenting in Double-Time" (Metallic chug)', cover: { bg: 'linear-gradient(135deg, #747d8c, #2f3542)', icon: '🔨', shadow: 'rgba(116,125,140,0.4)' } },
    { file: 'music/Brass March of the Djent-2.mp3', title: 'Steel-Toed Strutters',         flavor: 'By The Hard-Hat Horns • Album: "Industrial Parade Hazards" (~125bpm)', cover: { bg: 'linear-gradient(135deg, #2e86de, #f39c12)', icon: '🛡️', shadow: 'rgba(46,134,222,0.4)' } },
    { file: 'music/Brass March of the Djent-3.mp3', title: 'Iron Lung Fanfare',    flavor: 'By The Low-End Lung-Busters • Album: "Chugging at 180bpm" (Heavy march)', cover: { bg: 'linear-gradient(135deg, #cd84f1, #c56cf0)', icon: '🫁', shadow: 'rgba(205,132,241,0.4)' } },
    { file: 'music/Brass Balkan Chiptune.mp3',      title: 'Paprika Chiptune Parade',    flavor: 'By The 8-Bit Brass Bandits • Album: "High-Tempo Balkan Bytes"', cover: { bg: 'linear-gradient(135deg, #12cbc4, #1289a7)', icon: '🎮', shadow: 'rgba(18,203,196,0.4)' } },
    { file: 'music/Brass Balkan Chiptune-2.mp3',    title: 'Slivovitz Synth March',   flavor: 'By The Pixelated Pomp • Album: "Plums, Pixels & Trumpets"', cover: { bg: 'linear-gradient(135deg, #9b59b6, #8e44ad)', icon: '🎠', shadow: 'rgba(155,89,182,0.45)' } },
    { file: 'music/Brass March of Doom.mp3',        title: 'The End of the Parade Route', flavor: 'By The Armageddon Horns • Album: "Last Float Standing" (Slow doom march)', cover: { bg: 'linear-gradient(135deg, #2c3e50, #000000)', icon: '🌋', shadow: 'rgba(44,62,80,0.5)' } },
    { file: 'music/Brass March of Doom-2.mp3',      title: 'Apocalypse Drumline', flavor: 'By The Doomsday Drum Corps • Album: "March of the Four Horse-Plumes"', cover: { bg: 'linear-gradient(135deg, #e74c3c, #962d22)', icon: '👿', shadow: 'rgba(231,76,60,0.4)' } },
    { file: 'music/Iron Pixel Kolo.mp3',            title: '8-Bit Balkan Boot-Stomper', flavor: 'By The Silicon Stompers • Album: "Kolo Coding and Cornet Clatter"', cover: { bg: 'linear-gradient(135deg, #f1c40f, #d68910)', icon: '💃', shadow: 'rgba(241,196,15,0.4)' } },
    { file: 'music/Brass and Bone-3.mp3',           title: 'Ribcage Ragtime Glissando',     flavor: 'By The Skeleton Slides • Album: "Dem Bones, Dem Brass" (Bouncy trombone)', cover: { bg: 'linear-gradient(135deg, #7f8c8d, #34495e)', icon: '🩻', shadow: 'rgba(127,140,141,0.4)' } },
    { file: 'music/Brass and Bone-4.mp3',           title: 'Mausoleum March',     flavor: 'By The Graveyard Gombas • Album: "Ghost-Notes in the Key of G"', cover: { bg: 'linear-gradient(135deg, #1abc9c, #16a085)', icon: '🪦', shadow: 'rgba(26,188,156,0.4)' } },
    { file: 'music/Brass and Bone-5.mp3',           title: 'Marrow-Melting Trumpets',       flavor: 'By The Osteo-Orchestra • Album: "Bone-Shattering Fanfare"', cover: { bg: 'linear-gradient(135deg, #e67e22, #b9770e)', icon: '🎼', shadow: 'rgba(230,126,34,0.4)' } },
    { file: 'music/Balkan Brass Dubstep.mp3',       title: 'Balkan Bassdrop Blitz',        flavor: 'By The Dubstep Horn Division • Album: "Brass & Wobble" (Sub-bass Balkan march)', cover: { bg: 'linear-gradient(135deg, #e056fd, #686de0)', icon: '🔊', shadow: 'rgba(224,86,253,0.4)' } },
    { file: 'music/Balkan Brass Dubstep-2.mp3',     title: 'Sub-Woofer Tamburitza',        flavor: 'By The Heavy-Beat Horns • Album: "Wobble on the Danube" (Heavy bass brass)', cover: { bg: 'linear-gradient(135deg, #be2edd, #4834d4)', icon: '🎛️', shadow: 'rgba(190,46,221,0.4)' } },
    { file: 'music/Balkan Pixel Rain.mp3',          title: 'Balkan Pixel Rainstorm',       flavor: 'By The 8-Bit Accordion Guild • Album: "Chiptune Cloudburst"', cover: { bg: 'linear-gradient(135deg, #22a6b3, #00d2d3)', icon: '🌧️', shadow: 'rgba(34,166,179,0.4)' } },
    { file: 'music/Balkan Pixel Rain-2.mp3',        title: 'Digital Danube Downpour',      flavor: 'By The Byte-Sized Brass • Album: "Pixelated Pomp & Puddles"', cover: { bg: 'linear-gradient(135deg, #0984e3, #74b9ff)', icon: '⛈️', shadow: 'rgba(9,132,227,0.4)' } },
    { file: 'music/Brass Marching Beat.mp3',        title: 'Metronome Marchers',           flavor: 'By The Precision Percussion Platoon • Album: "Strictly On the One"', cover: { bg: 'linear-gradient(135deg, #eb4d4b, #ff7979)', icon: '🥁', shadow: 'rgba(235,77,75,0.4)' } },
    { file: 'music/Brass Marching Thunder.mp3',     title: 'Thunder-Rumble Trombones',     flavor: 'By The Low-Brass Stormtroopers • Album: "Lightning & Leather Boots"', cover: { bg: 'linear-gradient(135deg, #f0932b, #ffbe76)', icon: '🌩️', shadow: 'rgba(240,147,43,0.4)' } },
    { file: 'music/Brass Marching Thunder-2.mp3',   title: 'Sonic Boom Sousaphones',       flavor: 'By The Heavy Artillery Horns • Album: "Seismic Marching Band"', cover: { bg: 'linear-gradient(135deg, #badc58, #6ab04c)', icon: '💥', shadow: 'rgba(186,220,88,0.4)' } },
    { file: 'music/Brass and March.mp3',            title: 'The Forward Marching Cadence', flavor: 'By The Vanguard Valve Crew • Album: "Step in Time"', cover: { bg: 'linear-gradient(135deg, #30336b, #130cb7)', icon: '🎺', shadow: 'rgba(48,51,107,0.4)' } },
    { file: 'music/Brass and March-2.mp3',          title: 'High-Stepping Double Time',     flavor: 'By The Quick-Step Quartet • Album: "Polyester Sweat & Pride"', cover: { bg: 'linear-gradient(135deg, #e84393, #fd79a8)', icon: '👟', shadow: 'rgba(232,67,147,0.4)' } },
    { file: 'music/Five-Beat Brass.mp3',            title: 'The 5/4 Funky Fanfare',        flavor: 'By The Odd-Meter Orchestra • Album: "Count to Five & Strut"', cover: { bg: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', icon: '🖐️', shadow: 'rgba(108,92,231,0.4)' } },
    { file: 'music/March of the Brass.mp3',         title: 'March of the Brass Brigade',   flavor: 'By The Grand Avenue Marshals • Album: "Polished Bells & Epaulets"', cover: { bg: 'linear-gradient(135deg, #fdcb6e, #ffeaa7)', icon: '🎖️', shadow: 'rgba(253,203,110,0.4)' } },
    { file: 'music/March of the Brass-2.mp3',       title: 'Sovereign Horn Processional',  flavor: 'By The Royal Coronets • Album: "Crowns & Clefs"', cover: { bg: 'linear-gradient(135deg, #d63031, #ff7675)', icon: '🎷', shadow: 'rgba(214,48,49,0.4)' } },
    { file: 'music/Phrygian Taiko March.mp3',       title: 'Taiko & Trumpet Tempest',       flavor: 'By The East-West Drum Alliance • Album: "Phrygian Thunder & Brass"', cover: { bg: 'linear-gradient(135deg, #2d3436, #636e72)', icon: '🥁', shadow: 'rgba(45,52,54,0.4)' } },
    { file: 'music/Saffron Market March.mp3',       title: 'Village Processional',         flavor: 'By The Silk Road Brass Ensemble • Album: "Cardamom & Cornets"', cover: { bg: 'linear-gradient(135deg, #e67e22, #f39c12)', icon: '🕌', shadow: 'rgba(230,126,34,0.4)' } },
    { file: 'music/Sunrise Parade.mp3',             title: 'Dawn on the Parade Route',     flavor: 'By The Early Bird Horns • Album: "Dew on the Tuba & Fresh Coffee"', cover: { bg: 'linear-gradient(135deg, #f1c40f, #e67e22)', icon: '🌅', shadow: 'rgba(241,196,15,0.4)' } },
    { file: "music/Zmaj's Dubstep.mp3",             title: "Zmaj's Dragon Drop",          flavor: "By The Fire-Breathing Bass Cannon • Album: \"Lair of the Bass\"", cover: { bg: 'linear-gradient(135deg, #27ae60, #2ecc71)', icon: '🐉', shadow: 'rgba(39,174,96,0.4)' } },
    { file: "music/Zmaj's Dubstep-2.mp3",           title: "Dragon's Sub-Woofer Revenge", flavor: "By The Wyvern Wobble Syndicate • Album: \"Scales & Sub-Bass\"", cover: { bg: 'linear-gradient(135deg, #16a085, #1abc9c)', icon: '🐲', shadow: 'rgba(22,160,133,0.4)' } }
  ];

  const LS_KEY = 'pp_music_widget';

  const ICONS = {
    prev: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><polygon points="19 20 9 12 19 4 19 20" fill="currentColor" fill-opacity="0.15"></polygon><line x1="5" y1="19" x2="5" y2="5"></line></svg>`,
    play: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin-left:2px;"><polygon points="5 3 19 12 5 21 5 3" fill="currentColor" fill-opacity="0.25"></polygon></svg>`,
    pause: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" fill-opacity="0.25"></rect><rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" fill-opacity="0.25"></rect></svg>`,
    next: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><polygon points="5 4 15 12 5 20 5 4" fill="currentColor" fill-opacity="0.15"></polygon><line x1="19" y1="5" x2="19" y2="19"></line></svg>`,
    shuffle: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><polyline points="16 3 21 3 21 8"></polyline><line x1="4" y1="20" x2="21" y2="3"></line><polyline points="21 16 21 21 16 21"></polyline><line x1="15" y1="15" x2="21" y2="21"></line><line x1="4" y1="4" x2="9" y2="9"></line></svg>`
  };

  // Real-device testing confirmed Web Audio API's ctx.destination produces no
  // audible output at all on iOS native (Capacitor WKWebView) — every internal
  // signal (ctx.state, decoded buffer, started source, sample rate, live analyser
  // data) reports full success, yet nothing is heard, consistently and
  // reproducibly. A plain HTMLAudioElement (unconnected to any Web Audio graph —
  // connecting it via createMediaElementSource would route it through the very
  // same broken ctx.destination) was confirmed audible standalone. So iOS native
  // plays through a real <audio> element instead, bypassing Web Audio's output
  // path entirely; web (where Web Audio already works, which is WHY it was chosen
  // over <audio> here in the first place — see the file header) is untouched.
  const IS_NATIVE = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  const nativeAudio = () => (IS_NATIVE && window.Capacitor.Plugins && window.Capacitor.Plugins.NativeAudio) || null;

  // ---- Player (Web Audio on web; @capgo/native-audio on iOS native) --------
  const Player = {
    ctx: null,
    gain: null,
    _nativeMusicId: null,   // assetId of the currently-loaded native track (native only)
    buffers: {},        // file -> AudioBuffer (decode cache; unused on iOS native)
    source: null,
    index: 0,
    playing: false,
    shuffle: false,
    volume: 0.7,        // the widget's own slider level
    masterFactor: 1,    // external multiplier driven by Settings (master × music vol; 0 = muted)
    _req: 0,            // supersede token for async decodes

    // Effective output gain = widget slider × Settings-derived master factor, so the
    // Settings "Master/Music Volume" and "Mute Music" actually control playback.
    _effGain() { return this.volume * this.masterFactor; },
    setMasterFactor(f) {
      this.masterFactor = Math.max(0, Math.min(1, f == null ? 1 : f));
      if (this.gain) this.gain.gain.value = this._effGain();
      if (this._nativeMusicId) { const na = nativeAudio(); if (na) na.setVolume({ assetId: this._nativeMusicId, volume: this._effGain() }).catch(() => {}); }
    },

    _ensureCtx() {
      if (!this.ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AC();
        this.gain = this.ctx.createGain();
        this.gain.gain.value = this._effGain();
        // Passive tap for the header level meter: gain -> analyser -> destination.
        // The analyser doesn't alter the sound; it just exposes live spectrum data.
        this.analyser = this.ctx.createAnalyser();
        this.analyser.fftSize = 64;
        this.analyser.smoothingTimeConstant = 0.75;
        this.gain.connect(this.analyser);
        this.analyser.connect(this.ctx.destination);
      }
      return this.ctx;
    },

    _decode(arrBuf) {
      return new Promise((resolve, reject) => {
        let p;
        try { p = this.ctx.decodeAudioData(arrBuf, resolve, reject); }
        catch (e) { reject(e); return; }
        if (p && p.then) p.then(resolve, reject);
      });
    },

    async _buffer(file) {
      if (this.buffers[file]) return this.buffers[file];
      // Don't gate on r.ok/r.status: Capacitor's iOS WKURLSchemeHandler
      // (WebViewAssetHandler.swift) deliberately answers "media extension" files
      // (mp3/wav/m4a/aac/...) with a bare URLResponse instead of an HTTPURLResponse,
      // so fetch() sees status 0 / ok:false for these files specifically — even
      // though the real bytes are delivered correctly right after. Web and Android
      // (which don't have this quirk) still get a normal 200 here, so this doesn't
      // change behavior there; arrayBuffer() throwing is still the real failure signal.
      const arr = await fetch(file).then(r => r.arrayBuffer());
      const buf = await this._decode(arr.slice(0));
      this.buffers[file] = buf;
      return buf;
    },

    _stopSource() {
      if (this.source) {
        try { this.source.onended = null; this.source.stop(); } catch (e) {}
        try { this.source.disconnect(); } catch (e) {}
        this.source = null;
      }
    },

    // Music on iOS native goes through @capgo/native-audio. Unlike SFX (all
    // preloaded up front), tracks are loaded on demand: there are 50+ of them at
    // ~1.7MB each, so preloading the whole playlist would waste a lot of memory to
    // play one song. The previously-loaded track is unloaded first so only one
    // music asset is ever resident.
    // End-of-track advance for the NATIVE path.
    //
    // The web path gets this for free: playIndex() sets `src.onended = () => this.next()`
    // on the AudioBufferSourceNode. _playIndexNative returns long before that line, so on
    // iOS nothing was listening for a track finishing — a song played to its end and
    // playback simply stopped. That single gap caused BOTH reported symptoms: continuous
    // play did nothing, and shuffle never engaged either, because shuffle is only
    // consulted inside next(). The visible result was "it plays the same one file": press
    // play, hear track N, silence, press play, hear track N again.
    //
    // @capgo/native-audio emits 'complete' with { assetId } when an asset finishes.
    // Registered once, lazily, because the plugin only exists on native.
    _ensureNativeCompleteListener() {
      if (this._nativeCompleteBound) return;
      const na = nativeAudio();
      if (!na || typeof na.addListener !== 'function') return;
      this._nativeCompleteBound = true;
      try {
        na.addListener('complete', (info) => {
          const finished = info && info.assetId;
          // The assetId check is load-bearing, not defensive tidiness: the game's SOUND
          // EFFECTS go through this same plugin (see engine.preloadNativeSfx), so every
          // explosion and balloon pop also fires 'complete'. Without this guard the
          // playlist would skip forward on every sound effect.
          if (!finished || finished !== this._nativeMusicId) return;
          // Don't resurrect playback the player deliberately paused or stopped, and
          // ignore the completion that a stop() during track-switching can emit.
          if (!this.playing) return;
          this.next();
        }).catch(() => {});
      } catch (e) { /* listener is best-effort; manual controls still work without it */ }
    },

    async _playIndexNative(index) {
      const na = nativeAudio();
      if (!na) return;
      this._ensureNativeCompleteListener();
      this.index = ((index % TRACKS.length) + TRACKS.length) % TRACKS.length;
      const track = TRACKS[this.index];
      const myReq = ++this._req;
      this.playing = true;      // reflect intent immediately for the UI
      UI.render();

      // Release the previous track before loading the next.
      if (this._nativeMusicId && this._nativeMusicId !== track.file) {
        try { await na.stop({ assetId: this._nativeMusicId }); } catch (e) {}
        try { await na.unload({ assetId: this._nativeMusicId }); } catch (e) {}
        this._nativeMusicId = null;
      }

      const assetId = track.file;   // file path doubles as a stable unique id
      try {
        if (this._nativeMusicId !== assetId) {
          await na.preload({ assetId, assetPath: track.file, audioChannelNum: 1, isUrl: false });
          this._nativeMusicId = assetId;
        }
        if (myReq !== this._req) return;   // a newer play/stop superseded us
        await na.play({ assetId, volume: this._effGain() });
      } catch (e) {
        console.warn('[music] native play failed', track.file, e && e.message);
        if (myReq === this._req) { this.playing = false; UI.render(); }
        return;
      }
      if (myReq !== this._req) return;

      this.playing = true;
      try { if (this._chan) this._chan.postMessage({ type: 'claim', id: this._tabId }); } catch (e) {}
      this._save();
      UI.render();
      UI.showNowPlaying(track);
    },

    // Play track at `index`. Resumes the context (call from a gesture).
    async playIndex(index) {
      if (IS_NATIVE) return this._playIndexNative(index);

      const ctx = this._ensureCtx();
      this.index = ((index % TRACKS.length) + TRACKS.length) % TRACKS.length;
      const track = TRACKS[this.index];
      const myReq = ++this._req;
      this.playing = true;      // reflect intent immediately for the UI
      UI.render();
      if (ctx.state !== 'running') { try { await ctx.resume(); } catch (e) {} }

      let buf;
      try { buf = await this._buffer(track.file); }
      catch (e) {
        console.warn('[music] failed to load', track.file, e);
        if (myReq === this._req) { this.playing = false; UI.render(); }
        return;
      }
      if (myReq !== this._req) return;   // a newer play/stop superseded us

      // Atomic swap: start the new source, THEN stop the old one — never silent.
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.gain);
      src.onended = () => {                // natural end -> advance the playlist
        if (src === this.source) { this.source = null; this.next(); }
      };
      try { src.start(0); } catch (e) {}
      const old = this.source;
      this.source = src;
      if (old && old !== src) { try { old.onended = null; old.stop(); old.disconnect(); } catch (e) {} }
      this.playing = true;
      // Claim the single-instance lock: tell every other tab/window to yield so we
      // never stack multiple shuffles on top of each other.
      try { if (this._chan) this._chan.postMessage({ type: 'claim', id: this._tabId }); } catch (e) {}
      this.gain.gain.value = this._effGain();
      this._save();
      UI.render();
      UI.showNowPlaying(track);   // fresh "Now Playing" snackbar
    },

    play() { this.playIndex(this.index); },
    next() {
      if (this.shuffle && TRACKS.length > 1) {
        let n = this.index;
        while (n === this.index) n = Math.floor(Math.random() * TRACKS.length);
        this.playIndex(n);
      } else {
        this.playIndex(this.index + 1);
      }
    },
    prev() { this.playIndex(this.index - 1); },
    toggleShuffle() { this.shuffle = !this.shuffle; this._save(); },

    pause() {
      this._req++;              // cancel any in-flight decode/play
      if (IS_NATIVE) {
        if (this._nativeMusicId) { const na = nativeAudio(); if (na) na.pause({ assetId: this._nativeMusicId }).catch(() => {}); }
      } else {
        this._stopSource();
      }
      this.playing = false;
      this._save();
      UI.render();
    },

    toggle() { if (this.playing) this.pause(); else this.play(); },

    setVolume(v, silent) {
      this.volume = Math.max(0, Math.min(1, v));
      if (this.gain) this.gain.gain.value = this._effGain();
      if (this._nativeMusicId) { const na = nativeAudio(); if (na) na.setVolume({ assetId: this._nativeMusicId, volume: this._effGain() }).catch(() => {}); }
      this._save();
      if (this.volEl) this.volEl.value = this.volume;       // mirror onto the widget's own slider
      // The widget slider and the Settings "Music Volume" are the SAME level: echo any
      // widget-driven change back into Settings (skipped when Settings drove the change).
      if (!silent && typeof window.onWidgetMusicVolume === 'function') window.onWidgetMusicVolume(this.volume);
    },

    _save() {
      try { localStorage.setItem(LS_KEY, JSON.stringify({ index: this.index, volume: this.volume, shuffle: this.shuffle })); } catch (e) {}
    },
    _load() {
      try {
        const s = JSON.parse(localStorage.getItem(LS_KEY));
        if (s) {
          if (typeof s.index === 'number') this.index = s.index % TRACKS.length;
          if (typeof s.volume === 'number') this.volume = s.volume;
          if (typeof s.shuffle === 'boolean') this.shuffle = s.shuffle;
        }
      } catch (e) {}
    },

    // Cross-tab + lifecycle coordination. Fixes music that (a) stacked across browser
    // windows/tabs — including iPad Split View, where two windows are both "visible" —
    // and (b) kept playing after backgrounding, closing, or a hard reset. Rule: only ONE
    // tab makes sound at a time, hidden tabs go silent, and a torn-down page fully
    // releases its audio.
    _initLifecycle() {
      this._tabId = Math.random().toString(36).slice(2);

      // Single-instance lock: whenever any tab starts playing it broadcasts a claim, and
      // every OTHER tab that is currently playing yields (pauses) so they never overlap.
      try {
        if ('BroadcastChannel' in window) {
          this._chan = new BroadcastChannel('parade-music');
          this._chan.onmessage = (ev) => {
            const d = ev && ev.data;
            if (!d || d.id === this._tabId) return;
            if (d.type === 'claim' && this.playing) this.pause();
          };
        }
      } catch (e) {}

      const resumeIfPlaying = () => {
        if (this.playing && this.ctx && this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
        if (this.playing && this._nativeMusicId) { const na = nativeAudio(); if (na) na.resume({ assetId: this._nativeMusicId }).catch(() => {}); }
      };

      // Backgrounded tab -> go silent (suspend the context). Foregrounded again -> resume
      // if this was the tab meant to be playing. This is what stops several windows from
      // each shuffling over one another.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          if (this.ctx && this.ctx.state === 'running') { try { this.ctx.suspend(); } catch (e) {} }
          if (this._nativeMusicId) { const na = nativeAudio(); if (na) na.pause({ assetId: this._nativeMusicId }).catch(() => {}); }
        } else {
          resumeIfPlaying();
        }
      });
      // iOS often won't resume() outside a gesture, so also retry on the first touch/click.
      document.addEventListener('touchstart', resumeIfPlaying, { passive: true });
      document.addEventListener('click', resumeIfPlaying, { passive: true });

      // Page teardown (close / reload / navigate away, incl. iOS bfcache via pagehide):
      // stop the source and close the context so nothing survives a quit or hard reset.
      const teardown = () => {
        try { this._req++; this._stopSource(); this.playing = false; } catch (e) {}
        try { if (this.ctx && this.ctx.state !== 'closed') this.ctx.close(); } catch (e) {}
        try { if (this._nativeMusicId) { const na = nativeAudio(); if (na) { na.stop({ assetId: this._nativeMusicId }).catch(() => {}); na.unload({ assetId: this._nativeMusicId }).catch(() => {}); } } } catch (e) {}
        this.ctx = null; this.gain = null; this.analyser = null; this.buffers = {}; this._nativeMusicId = null;
        try { if (this._chan) this._chan.close(); } catch (e) {}
      };
      window.addEventListener('pagehide', teardown);
      window.addEventListener('beforeunload', teardown);
    }
  };

  // ---- UI -------------------------------------------------------------------
  const UI = {
    root: null, panel: null, rows: [], playBtn: null, nowEl: null, volEl: null, open: false,

    build() {
      // Inject glassmorphic style rules
      const style = document.createElement('style');
      style.textContent = `
        /* Custom scrollbar for tracklist */
        #pp-music-panel .track-list-container::-webkit-scrollbar {
          width: 5px;
        }
        #pp-music-panel .track-list-container::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.01);
        }
        #pp-music-panel .track-list-container::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.12);
          border-radius: 4px;
        }
        #pp-music-panel .track-list-container::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        /* Panel entry/exit transition */
        .pp-glass-panel {
          display: block !important;
          opacity: 0;
          transform: translateY(-8px) scale(0.96);
          pointer-events: none;
          transition: opacity 0.22s cubic-bezier(0.16, 1, 0.3, 1), transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .pp-glass-panel.open {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }

        /* High-end glass button */
        .pp-glass-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.85);
          cursor: pointer;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          flex-shrink: 0;
          outline: none;
        }
        .pp-glass-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.25);
          color: #fff;
          transform: scale(1.08);
        }
        .pp-glass-btn:active {
          transform: scale(0.95);
        }

        /* Play button special styling */
        .pp-glass-btn-play {
          background: linear-gradient(135deg, rgba(126, 231, 135, 0.25) 0%, rgba(30, 215, 96, 0.15) 100%);
          border: 1px solid rgba(126, 231, 135, 0.4);
          color: #7ee787;
          box-shadow: 0 4px 12px rgba(126, 231, 135, 0.15);
        }
        .pp-glass-btn-play:hover {
          background: linear-gradient(135deg, rgba(126, 231, 135, 0.35) 0%, rgba(30, 215, 96, 0.25) 100%);
          border-color: rgba(126, 231, 135, 0.7);
          box-shadow: 0 6px 18px rgba(126, 231, 135, 0.3), 0 0 0 1px rgba(126, 231, 135, 0.2);
          color: #a1f3b0;
          transform: scale(1.12);
        }

        /* Beautiful slider track */
        .pp-glass-range {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 2px;
          background: rgba(255, 255, 255, 0.12);
          outline: none;
          transition: background 0.2s;
        }
        .pp-glass-range::-webkit-slider-runnable-track {
          width: 100%;
          height: 4px;
          cursor: pointer;
        }
        .pp-glass-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #7ee787;
          cursor: pointer;
          margin-top: -4px;
          box-shadow: 0 0 8px rgba(126, 231, 135, 0.6);
          transition: transform 0.15s, background-color 0.15s;
        }
        .pp-glass-range::-webkit-slider-thumb:hover {
          transform: scale(1.3);
          background: #a1f3b0;
        }

        /* List rows */
        .pp-track-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          cursor: pointer;
          border-bottom: 1px solid rgba(255, 255, 255, 0.03);
          transition: all 0.2s ease;
        }
        .pp-track-row:hover {
          background: rgba(255, 255, 255, 0.04);
        }
        .pp-track-row.active {
          background: linear-gradient(90deg, rgba(126, 231, 135, 0.08) 0%, rgba(126, 231, 135, 0.01) 100%);
        }

        /* Toggle icon pulse when playing */
        #pp-music-toggle.playing {
          animation: pulse-music 1.8s infinite ease-in-out;
        }
        @keyframes pulse-music {
          0% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(126,231,135,0.4)); }
          50% { transform: scale(1.08); filter: drop-shadow(0 0 8px rgba(126,231,135,0.8)); }
          100% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(126,231,135,0.4)); }
        }
        /* Live level meter (equalizer) shown while music plays */
        #pp-music-toggle { position: relative; display: inline-flex; align-items: center; }
        #pp-music-toggle.playing { animation: none; } /* meter conveys activity; drop the whole-button pulse while playing */
        .pp-eq { display: none; align-items: flex-end; justify-content: center; gap: 2px; height: 15px; margin-left: 5px; pointer-events: none; }
        .pp-eq i { width: 3px; height: 3px; border-radius: 1.5px; background: #7ee787; box-shadow: 0 0 4px rgba(126,231,135,0.7); transition: height .07s linear; }
        #pp-music-toggle.playing .pp-eq { display: flex; }
        @media (prefers-reduced-motion: reduce) { .pp-eq i { transition: none; } }
        @keyframes pp-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);

      // Toggle button — placed INSIDE the top-bar controls so it lives in the
      // header next to the other icon buttons (no floating overlap with Start Wave).
      const btn = document.createElement('button');
      btn.id = 'pp-music-toggle';
      btn.title = 'Music';
      btn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7ee787" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" style="display:block;pointer-events:none;">
        <path d="M9 18V5l12-2v13"></path>
        <circle cx="6" cy="18" r="3" fill="#7ee787"></circle>
        <circle cx="18" cy="16" r="3" fill="#7ee787"></circle>
      </svg>`;
      btn.onclick = (e) => { e.stopPropagation(); this.setOpen(!this.open); };
      const host = document.querySelector('.top-bar-controls-container');
      if (host) {
        btn.className = 'icon-btn';           // inherit the header button styling
        const ham = host.querySelector('.hamburger-btn');
        host.insertBefore(btn, ham || null);  // sit just before the ☰ menu
      } else {
        // Fallback if the header isn't present: a small fixed top-right button.
        btn.style.cssText = css(`position:fixed;top:64px;right:16px;z-index:99998;width:40px;height:40px;
          border-radius:50%;border:1px solid rgba(255,255,255,.15);background:rgba(18,18,30,.92);color:#fff;
          font-size:18px;cursor:pointer;`);
        document.body.appendChild(btn);
      }
      this.toggleBtn = btn;

      // --- Live level meter: swap the note for animated EQ bars while playing ---
      const noteSvg = btn.querySelector('svg');
      if (noteSvg) noteSvg.classList.add('pp-note');
      const eq = document.createElement('span');
      eq.className = 'pp-eq';
      const NBARS = 4;
      for (let i = 0; i < NBARS; i++) eq.appendChild(document.createElement('i'));
      btn.appendChild(eq);
      const bars = eq.querySelectorAll('i');
      const freq = new Uint8Array(32);        // fftSize 64 -> 32 bins
      const binIdx = [1, 3, 6, 10];           // low→mid spread that dances nicely
      const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      let phase = 0;
      // The meter used to self-schedule an rAF FOREVER at the display rate (120Hz on
      // ProMotion) even when paused, writing DOM styles 4×/frame while playing. Now it
      // runs a ~18fps timer only WHILE playing and stops itself when idle — restarted on
      // demand from UI.render() when playback resumes. Plenty smooth for a 4-bar meter.
      let meterTimer = null;
      const tick = () => {
        meterTimer = null;
        if (!Player.playing) { btn.classList.remove('playing'); return; } // stop the loop
        btn.classList.add('playing');
        if (reduceMotion) {
          for (let i = 0; i < NBARS; i++) bars[i].style.height = '9px';
        } else {
          const an = Player.analyser;
          if (an) {
            an.getByteFrequencyData(freq);
            for (let i = 0; i < NBARS; i++) {
              const v = freq[binIdx[i]] / 255;
              bars[i].style.height = (3 + Math.round(v * 12)) + 'px';
            }
          } else {
            phase += 0.25;
            for (let i = 0; i < NBARS; i++) bars[i].style.height = (6 + Math.round(4 * Math.abs(Math.sin(phase + i)))) + 'px';
          }
        }
        meterTimer = setTimeout(tick, 55);
      };
      this._startMeter = () => { if (!meterTimer && Player.playing) tick(); };
      if (Player.playing) this._startMeter();

      // Panel
      const panel = document.createElement('div');
      panel.id = 'pp-music-panel';
      panel.className = 'pp-glass-panel';
      panel.style.cssText = css(`display:none;`);
      this._panelDisplay = 'block';

      // Header
      const head = document.createElement('div');
      head.style.cssText = css(`display:flex;align-items:center;justify-content:space-between;
        padding:12px 16px;border-bottom:1px solid rgba(255,255,255,.06);`);
      head.innerHTML = '<b style="font-size:14px;letter-spacing:.3px;background:linear-gradient(90deg,#7ee787,#a1f3b0);-webkit-background-clip:text;-webkit-text-fill-color:transparent;">🎶 Parade Panic Radio</b>';
      const close = document.createElement('button');
      close.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" style="display:block;">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>`;
      close.style.cssText = css('background:none;border:none;color:#9aa;cursor:pointer;transition:color .2s;');
      close.onmouseenter = () => close.style.color = '#fff';
      close.onmouseleave = () => close.style.color = '#9aa';
      close.onclick = () => this.setOpen(false);
      head.appendChild(close);
      panel.appendChild(head);

      // Now playing Card
      const nowContainer = document.createElement('div');
      nowContainer.style.cssText = css(`margin:12px 14px;padding:10px 12px;
        background:linear-gradient(135deg, rgba(126,231,135,0.06) 0%, rgba(255,255,255,0.01) 100%);
        border:1px solid rgba(126,231,135,0.12);border-radius:12px;
        display:flex;flex-direction:row;align-items:center;gap:12px;overflow:hidden;`);
      
      const artContainer = document.createElement('div');
      artContainer.style.cssText = css('width:42px;height:42px;min-width:42px;border-radius:10px;overflow:hidden;flex-shrink:0;');
      
      const infoContainer = document.createElement('div');
      infoContainer.style.cssText = css('flex:1;min-width:0;display:flex;flex-direction:column;gap:1px;');
      
      const nowLabel = document.createElement('div');
      nowLabel.textContent = 'NOW PLAYING';
      nowLabel.style.cssText = css('font-size:9px;font-weight:700;letter-spacing:1px;color:#889;text-transform:uppercase;');
      
      const nowText = document.createElement('div');
      nowText.style.cssText = css('color:#7ee787;font-weight:600;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;');
      
      const flavorText = document.createElement('div');
      flavorText.style.cssText = css('font-size:10px;color:#889;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;');
      
      infoContainer.append(nowLabel, nowText, flavorText);
      nowContainer.append(artContainer, infoContainer);
      panel.appendChild(nowContainer);
      this.nowEl = nowText;
      this.nowArtEl = artContainer;
      this.nowFlavorEl = flavorText;

      // Transport
      const bar = document.createElement('div');
      bar.style.cssText = css('display:flex;align-items:center;gap:10px;padding:0 14px 14px;');
      const mk = (iconSvg, fn, isPlay) => {
        const b = document.createElement('button');
        b.className = isPlay ? 'pp-glass-btn pp-glass-btn-play' : 'pp-glass-btn';
        b.innerHTML = iconSvg; b.onclick = fn;
        b.style.cssText = css(`width:${isPlay ? 44 : 34}px;height:${isPlay ? 44 : 34}px;`);
        return b;
      };
      const prev = mk(ICONS.prev, () => Player.prev(), false);
      this.playBtn = mk(ICONS.play, () => Player.toggle(), true);
      const next = mk(ICONS.next, () => Player.next(), false);
      this.shuffleBtn = mk(ICONS.shuffle, () => { Player.toggleShuffle(); this.render(); }, false);
      this.shuffleBtn.title = 'Shuffle';
      
      bar.append(prev, this.playBtn, next, this.shuffleBtn);

      // Volume
      const vol = document.createElement('input');
      vol.type = 'range'; vol.min = 0; vol.max = 1; vol.step = 0.01; vol.value = Player.volume;
      vol.className = 'pp-glass-range';
      vol.style.cssText = css('width:80px;flex:none;margin-left:auto;cursor:pointer;');
      vol.oninput = () => Player.setVolume(parseFloat(vol.value));
      bar.appendChild(vol);
      this.volEl = vol;
      panel.appendChild(bar);

      // Track list
      const list = document.createElement('div');
      list.className = 'track-list-container';
      list.style.cssText = css('max-height:180px;overflow-y:auto;-webkit-overflow-scrolling:touch;border-top:1px solid rgba(255,255,255,.06);');
      this.rows = TRACKS.map((t, i) => {
        const row = document.createElement('div');
        row.className = 'pp-track-row';
        row.onclick = () => Player.playIndex(i);
        const num = document.createElement('span');
        num.textContent = (i + 1);
        num.style.cssText = css('width:18px;color:#889;font-variant-numeric:tabular-nums;flex:none;');
        const title = document.createElement('span');
        title.textContent = t.title;
        title.style.cssText = css('white-space:nowrap;overflow:hidden;text-overflow:ellipsis;');
        row.append(num, title);
        row._num = num; row._title = title;
        list.appendChild(row);
        return row;
      });
      panel.appendChild(list);

      document.body.appendChild(panel);
      this.panel = panel;

      // Click outside to close panel (excluding clicks inside the panel or on the toggle button)
      document.addEventListener('click', (e) => {
        if (this.open && this.panel && !this.panel.contains(e.target) && !this.toggleBtn.contains(e.target)) {
          this.setOpen(false);
        }
      });

      this.render();
    },

    setOpen(v) {
      this.open = v;
      if (this.panel) {
        this.panel.classList.toggle('open', v);
      }
    },

    render() {
      if (!this.panel) return;
      const cur = TRACKS[Player.index];
      if (this.playBtn) this.playBtn.innerHTML = Player.playing ? ICONS.pause : ICONS.play;
      if (this.shuffleBtn) {
        const s = this.shuffleBtn.style;
        if (Player.shuffle) {
          s.background = 'rgba(126,231,135,.22)';
          s.border = '1px solid rgba(126,231,135,.55)';
          s.color = '#7ee787';
          s.boxShadow = 'inset 0 1px 0 rgba(255,255,255,.25), 0 0 12px rgba(126,231,135,.4)';
        } else {
          s.background = 'rgba(255,255,255,.05)';
          s.border = '1px solid rgba(255,255,255,.08)';
          s.color = 'rgba(255,255,255,.85)';
          s.boxShadow = 'none';
        }
        this.shuffleBtn.title = Player.shuffle ? 'Shuffle: on' : 'Shuffle: off';
      }
      if (this.nowEl) this.nowEl.textContent = cur ? cur.title : '—';
      if (this.nowArtEl) this.nowArtEl.innerHTML = getCoverArt(cur, 42, true);
      if (this.nowFlavorEl) {
        this.nowFlavorEl.textContent = cur ? (cur.flavor || '') : '';
        this.nowFlavorEl.style.display = cur ? 'block' : 'none';
      }
      if (this.toggleBtn) {
        this.toggleBtn.classList.toggle('playing', Player.playing);
      }
      if (Player.playing && this._startMeter) this._startMeter(); // (re)start the idle-stopping meter loop
      this.rows.forEach((row, i) => {
        const active = i === Player.index;
        row.classList.toggle('active', active);
        row._num.textContent = (active && Player.playing) ? '♪' : (i + 1);
        row._num.style.color = active ? '#7ee787' : '#889';
        row._title.style.color = active ? '#7ee787' : '#fff';
        row._title.style.fontWeight = active ? '600' : '400';
      });
      if (this.volEl && document.activeElement !== this.volEl) this.volEl.value = Player.volume;
    },

    // Fresh, self-contained "Now Playing" snackbar — glassmorphic pill that slides
    // up bottom-center when a track starts, then auto-dismisses.
    _toast: null, _toastTimer: null,
    showNowPlaying(track) {
      if (!track) return;
      if (!this._toast) {
        const t = document.createElement('div');
        t.id = 'pp-music-toast';
        t.style.cssText = css(`position:fixed;left:50%;bottom:22px;transform:translate(-50%,14px);
          z-index:99997;display:flex;align-items:center;gap:12px;max-width:min(420px,90vw);
          padding:10px 18px 10px 14px;border-radius:16px;
          background:rgba(16,16,26,.9);border:1px solid rgba(255,255,255,.14);
          box-shadow:0 10px 34px rgba(0,0,0,.55);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);
          color:#fff;font:13px system-ui,sans-serif;pointer-events:none;
          opacity:0;transition:opacity .3s ease, transform .3s ease;`);
        t.innerHTML =
          '<div id="pp-toast-art" style="flex-shrink:0;"></div>' +
          '<div style="min-width:0;flex:1;display:flex;flex-direction:column;gap:2px;">' +
            '<div style="font-size:10px;letter-spacing:1.6px;text-transform:uppercase;color:#7ee787;font-weight:800;line-height:1;">Now Playing</div>' +
            '<div id="pp-toast-title" style="font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:13px;line-height:1.2;"></div>' +
            '<div id="pp-toast-flavor" style="font-size:11px;color:#9aa;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.2;"></div>' +
          '</div>';
        document.body.appendChild(t);
        this._toast = t;
      }
      this._toast.querySelector('#pp-toast-art').innerHTML = getCoverArt(track, 40, true);
      this._toast.querySelector('#pp-toast-title').textContent = track.title;
      const fl = this._toast.querySelector('#pp-toast-flavor');
      fl.textContent = track.flavor || '';
      fl.style.display = track.flavor ? 'block' : 'none';
      // slide up + fade in
      const t = this._toast;
      requestAnimationFrame(() => { t.style.opacity = '1'; t.style.transform = 'translate(-50%,0)'; });
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        t.style.opacity = '0'; t.style.transform = 'translate(-50%,14px)';
      }, 3200);
    }
  };

  function css(s) { return s.replace(/\s+/g, ' ').trim(); }
  
  function getCoverArt(track, size = 44, rotate = false) {
    const cover = (track && track.cover) || {
      bg: 'linear-gradient(135deg, #334155, #1e293b)',
      icon: '🎵',
      shadow: 'rgba(0,0,0,0.3)'
    };
    const spinClass = ''; // art no longer spins (player + now-playing popup stay still)
    const iconSize = Math.round(size * 0.52);
    const renderedIcon = (typeof window.GameIcons !== 'undefined' && window.GameIcons.get)
      ? window.GameIcons.get(cover.icon, iconSize, 'display:block;')
      : cover.icon;
    return `<div style="width:${size}px;height:${size}px;min-width:${size}px;min-height:${size}px;
      border-radius:10px;background:${cover.bg};box-shadow:0 4px 12px ${cover.shadow};
      display:flex;align-items:center;justify-content:center;font-size:${iconSize}px;
      user-select:none;flex-shrink:0;${spinClass}">
      ${renderedIcon}
    </div>`;
  }

  function init() {
    Player._load();
    Player._initLifecycle();
    UI.build();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Expose for debugging / external control (e.g. demo mode closes the panel).
  window.paradeMusic = Player;
  window.paradeMusicUI = UI;
})();
